/* eslint-disable react/style-prop-object */
import React from 'react';
import ResponsiveBar from 'Common/Components/LazyResponsiveBar';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';
import { useTimelineTransactions } from 'ShorePay/hooks/transactions';
import dayjs from 'dayjs';
import { TransactionTimelineItem } from 'utils/apiClient/services/shorepay/types';
import { Empty } from 'antd';
import { findIndex, isEmpty, propEq } from 'ramda';
import styled from 'styled-components';
import Loading from 'Common/Components/Loading';

interface Props {
  currency: string;
  data?: any;
}

const filters = {
  created_at_after: dayjs().subtract(30, 'days').toISOString(),
};

const transformResponseToChartData = (i: TransactionTimelineItem) => ({
  revenue: i.total_amount,
  date: i.start_date,
});

const NoData = () => (
  <Empty
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={<FormattedMessage defaultMessage="No transactions yet" />}
  />
);

const ChartWrapper = styled.div`
  width: 100%;
  height: 280px;
`;

const RevenueOverviewChart: React.FC<Props> = ({ currency }) => {
  const intl = useIntl();
  const tQuery = useTimelineTransactions(filters);

  if (tQuery.isLoading)
    return (
      <ChartWrapper>
        <Loading noText verticallyCentered />
      </ChartWrapper>
    );

  if (tQuery.isSuccess) {
    if (isEmpty(tQuery.data))
      return (
        <ChartWrapper>
          <NoData />
        </ChartWrapper>
      );

    return (
      <ChartWrapper>
        <ResponsiveBar
          data={tQuery.data.map(transformResponseToChartData)}
          keys={['revenue']}
          indexBy="date"
          margin={{ top: 35, right: 20, bottom: 20, left: 70 }}
          padding={0.6}
          colors="#dac0d2"
          enableLabel={false}
          legendLabel={() => intl.formatMessage({ defaultMessage: 'Revenue' })}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'top',
              direction: 'row',
              justify: false,
              itemWidth: 70,
              itemHeight: 25,
              translateY: -32,
              itemsSpacing: 10,
              symbolShape: 'circle',
              symbolSize: 20,
            },
          ]}
          axisLeft={{
            format: (value) =>
              intl.formatNumber(Number(value), {
                style: 'currency',
                currency,
              }),
          }}
          axisBottom={{
            format: (value) => {
              const i = findIndex(propEq('start_date', value), tQuery.data);
              return i % 2 === 0
                ? intl.formatDate(value, {
                    day: '2-digit',
                    month: '2-digit',
                  })
                : '';
            },
          }}
          tooltip={({ data: item }) => (
            <div
              css={`
                background: #333;
                color: #fff;
                padding: 4px 6px;
                border-radius: 2px;
              `}
            >
              <FormattedDate value={item.date} />:{' '}
              <span css="font-weight: 500">
                <FormattedNumber
                  value={parseFloat(item.revenue)}
                  style="currency"
                  currency={currency}
                />
              </span>
            </div>
          )}
        />
      </ChartWrapper>
    );
  }
  return null;
};

export default RevenueOverviewChart;
